import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Errore imprevisto";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  return String(d);
}

export default function AgenziaAuth({ mode = "login" }) {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nome, setNome] = useState("");
  const [agenziaNome, setAgenziaNome] = useState("");
  const [piva, setPiva] = useState("");
  const [busy, setBusy] = useState(false);
  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Compila email e password"); return; }
    if (isRegister) {
      if (!nome.trim()) { toast.error("Inserisci il nome referente"); return; }
      if (!agenziaNome.trim()) { toast.error("Inserisci il nome del Professionista incaricato / Studio"); return; }
    }
    setBusy(true);
    try {
      const url = isRegister ? "/user-auth/register" : "/user-auth/login";
      const body = isRegister
        ? { email, password, nome, account_type: "agenzia", agenzia_nome: agenziaNome, agenzia_piva: piva, agenzia_referente: nome }
        : { email, password };
      const r = await axios.post(`${API}${url}`, body);
      // Enforce: this login page is ONLY for agency accounts. If a locatore
      // mis-navigated here, send them to the right dashboard instead.
      if ((r.data.account_type || "").toLowerCase() !== "agenzia") {
        if (!isRegister) {
          toast.error("Questo è il login Professionista incaricato. Usa il pannello Locatore.");
          nav("/utente/login");
          return;
        }
      }
      localStorage.setItem("cc_user_token", r.data.access_token);
      localStorage.setItem("cc_user_nome", r.data.nome || "");
      localStorage.setItem("cc_user_email", r.data.email || email);
      localStorage.setItem("cc_user_account_type", r.data.account_type || "agenzia");
      localStorage.setItem("cc_user_agenzia_nome", r.data.agenzia_nome || agenziaNome || "");
      toast.success(isRegister ? "Account Professionista incaricato creato" : "Bentornati");
      nav("/agenzia/dashboard");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-slate-50 to-white flex items-center justify-center p-6" data-testid={`agenzia-${mode}-page`}>
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-lg p-8">
        <div className="flex items-center gap-2 mb-1">
          <Building2 size={16} className="text-indigo-700" />
          <p className="text-[11px] uppercase tracking-widest text-indigo-700 font-semibold">Unioncasa · Pannello Professionista incaricato</p>
        </div>
        <h1 className="text-2xl font-serif text-slate-900 mb-6">
          {isRegister ? "Registra il tuo studio / attività" : "Accedi al pannello Professionista incaricato"}
        </h1>
        <form onSubmit={submit} className="space-y-4">
          {isRegister && (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Nome Professionista incaricato / Studio *</label>
                <input type="text" value={agenziaNome} onChange={e=>setAgenziaNome(e.target.value)}
                  placeholder="Es. Studio Rossi — Consulenza Immobiliare"
                  className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:border-indigo-700 text-sm"
                  data-testid="agenzia-nome-input" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">P.IVA</label>
                <input type="text" value={piva} onChange={e=>setPiva(e.target.value)}
                  placeholder="01234567890"
                  className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:border-indigo-700 text-sm"
                  data-testid="agenzia-piva-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Nome referente *</label>
                <input type="text" value={nome} onChange={e=>setNome(e.target.value)}
                  placeholder="Es. Laura Bianchi"
                  className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:border-indigo-700 text-sm"
                  data-testid="agenzia-referente-input" required />
              </div>
            </>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Email di riferimento *</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
              placeholder="info@studioprofessionista.it"
              className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:border-indigo-700 text-sm"
              data-testid="agenzia-email-input" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Password *</label>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
              className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:border-indigo-700 text-sm"
              data-testid="agenzia-password-input" required minLength={isRegister ? 8 : 1} />
            {isRegister && <p className="text-[11px] text-slate-500 mt-1">Minimo 8 caratteri.</p>}
          </div>
          <button type="submit" disabled={busy}
            className="w-full h-11 rounded-md bg-indigo-700 text-white text-sm font-semibold hover:bg-indigo-800 disabled:opacity-60"
            data-testid="agenzia-submit-btn">
            {busy ? "Attendere..." : (isRegister ? "Registra account" : "Accedi")}
          </button>
        </form>
        {!isRegister && (
          <p className="text-xs text-slate-500 mt-3 text-right">
            <Link to="/utente/password-dimenticata" className="hover:text-slate-900 hover:underline" data-testid="agenzia-forgot-link">
              Password dimenticata?
            </Link>
          </p>
        )}
        <p className="text-xs text-slate-600 mt-6 text-center">
          {isRegister ? (
            <>Hai già un account Professionista incaricato? <Link to="/agenzia/login" className="text-indigo-700 font-semibold underline">Accedi</Link></>
          ) : (
            <>Non hai un account Professionista incaricato? <Link to="/agenzia/register" className="text-indigo-700 font-semibold underline">Registrati</Link></>
          )}
        </p>
        <p className="text-[11px] text-slate-400 mt-4 text-center">
          Sei un locatore? <Link to="/utente/login" className="underline hover:text-slate-700">Vai al pannello Locatore</Link>
        </p>
      </div>
    </div>
  );
}
